import { attentionTierLabel } from "@healthos/shared";
import { CalendarPlus, CaretDown, Check, Copy, Play } from "@phosphor-icons/react";
import { type ReactNode, useRef, useState } from "react";
import { cn } from "../../lib/cn";
import { useIsClamped } from "../../lib/use-is-clamped";
import { CARD_BODY, CARD_EYEBROW } from "../../primitives/type";
import { MICROSTEP_BEHAVIOUR_VISUALS } from "../microstep-behaviour-visual";
import { AttentionChoices } from "./choices";
import { AttentionContentPreview } from "./content-preview";
import { HoldPrimaryButton } from "./hold-primary";
import { AttentionJourney } from "./journey";
import { AttentionMediaBlock, mediaSubtitleFor } from "./media";
import { AttentionHeader, MediaMetaRow } from "./meta";
import { isLightSurface, opacityMix } from "./palette";
import { AttentionReflectionLookback } from "./reflection-lookback";
import { AttentionSignals } from "./signals";
import type { AttentionCardLargeData, AttentionCardLargeProps } from "./types";

export { isLightSurface } from "./palette";
export type {
  AttentionCardLargeData,
  AttentionCardLargeProps,
  AttentionChoice,
  AttentionContentPreviewItem,
  AttentionContentPreviewKind,
  AttentionJourney,
  AttentionJourneyStep,
  AttentionMedia,
  AttentionMediaKind,
  AttentionReflectionRow,
  AttentionTier,
  AttentionWatchingItem,
} from "./types";

/** What Heartbeat decided before this card, on the same disclosure the quiet card carries. */
function DecisionsDisclosure({
  decisions,
  dividerBorder,
  onToggle,
  opacityMix,
}: {
  decisions?: ReactNode;
  dividerBorder: string;
  onToggle?: (open: boolean) => void;
  opacityMix: (percent: number) => string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6 border-t pt-3" style={{ borderColor: dividerBorder }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          setOpen(next);
          onToggle?.(next);
        }}
        className={cn(
          CARD_EYEBROW,
          "inline-flex items-center gap-1.5 bg-transparent transition-colors duration-200",
        )}
        style={{ color: opacityMix(75) }}
      >
        {open ? "Hide what it decided" : "What it decided"}
        <CaretDown
          size={12}
          weight="bold"
          aria-hidden="true"
          className={cn("transition-transform duration-200", open && "rotate-180")}
        />
      </button>
      {open && decisions ? (
        <div className="pt-3 motion-safe:animate-fade-in-up">{decisions}</div>
      ) : null}
    </div>
  );
}

export function AttentionCardLarge({
  data,
  onPrimary,
  onGhost,
  onWhy,
  onDismiss,
  className,
  completed = false,
  completedMessage,
  completedSubtitle,
  tier = "routine",
  holdToComplete = false,
  holdHint = false,
  feedbackAction,
  variant = "default",
  decisions,
  onToggleDecisions,
  choices,
  chosen,
  onChoose,
  choosing = false,
}: AttentionCardLargeProps) {
  const hasMedia = Boolean(data.media);
  const useCompactLayout = true;
  const isMicrostepCard = !hasMedia && data.primaryAction?.toLowerCase() === "mark as done";
  const showPlayIcon = data.media?.kind === "reset" || data.media?.kind === "guide";
  // The surface's light/dark identity is decided once here and emitted as
  // data-surface; attention-card-large.css sets --attn-ink from it, and every
  // colour below is a mix against that inherited var, so ink cannot drift from
  // the surface it sits on.
  const isLight = isLightSurface(tier, variant, hasMedia);
  const tierLabel = attentionTierLabel(tier);
  const hasWatching = Boolean(data.watching && data.watching.length > 0);
  // Day-zero cards keep theirs open: "What's waiting there" is the card. A card
  // that fills its content slot with the plan mosaic or the reflection look-back
  // has no signal rows to reveal, so it earns no toggle.
  const hasOwnContentPanel =
    data.planPreview !== undefined || data.reflectionLookback !== undefined;
  const collapsibleSignals = hasWatching && !data.journey && !hasOwnContentPanel;
  // Lifted out of WhyBlock so one disclosure governs both the full rationale
  // and the signals panel. A worded card is short enough to show its evidence
  // at rest; a card carrying a player keeps the panel behind the toggle so the
  // player, not the audit, is what a member lands on.
  const openAtRest = collapsibleSignals && !data.media;
  const [expanded, setExpanded] = useState(openAtRest);
  // The card is not remounted between actions, so without this a member who
  // opened More context on one card would meet the next one already expanded.
  // Same derived-state reset the rail uses for its promoted peek.
  const [prevHeadline, setPrevHeadline] = useState(data.headline);
  if (prevHeadline !== data.headline) {
    setPrevHeadline(data.headline);
    setExpanded(openAtRest);
  }
  const inkPrimary = "var(--attn-ink)";
  const inkSecondary = opacityMix(82);
  // Accent, not ink: the CTA is the brand's primary action colour. On the dark
  // bundle accent and ink are both paper, so timely and urgent are unchanged.
  const primaryBg = "var(--attn-accent)";
  const primaryInk = "var(--attn-primary-ink)";
  const watchingBorder = "var(--attn-watching-border)";
  const dividerBorder = "var(--attn-divider)";

  const headline = (
    <AttentionHeadline
      data={data}
      inkPrimary={inkPrimary}
      isCompactCard={useCompactLayout}
      onPrimary={onPrimary}
      opacityMix={opacityMix}
    />
  );

  const liveBody = (
    <>
      {data.journey ? null : headline}

      <div
        className={cn(
          "attention-card-body relative z-10",
          // Only first-run cards grow: the journey rail pins to the bottom with
          // mt-auto, which turns the empty lower half into the route itself.
          data.journey && "flex min-h-0 flex-1 flex-col",
          hasMedia && "px-6 pt-6 pb-6 sm:px-8 sm:pt-7 lg:px-10 lg:pt-8",
        )}
      >
        {hasMedia ? (
          <MediaMetaRow data={data} inkSecondary={inkSecondary} tier={tier} tierLabel={tierLabel} />
        ) : null}

        <div className={cn(data.journey && "flex flex-1 flex-col justify-center")}>
          {data.journey ? headline : null}
          <WhyBlock
            data={data}
            isCompactCard={useCompactLayout}
            opacityMix={opacityMix}
            watchingBorder={watchingBorder}
            expanded={expanded}
            onToggle={() => setExpanded((value) => !value)}
            revealsSignals={collapsibleSignals}
          />

          {/* One content slot, three possible fills. A caller sets at most one
              of planPreview / reflectionLookback; otherwise the slot holds the
              signal rows, collapsed behind More context unless a card has
              earned an always-open panel. */}
          {choices?.length ? (
            <AttentionChoices
              busy={choosing}
              choices={choices}
              chosen={chosen}
              label={data.signalsLabel}
              onChoose={onChoose}
              opacityMix={opacityMix}
            />
          ) : data.planPreview !== undefined ? (
            <AttentionContentPreview
              isLight={isLight}
              items={data.planPreview}
              opacityMix={opacityMix}
              pending={data.planPreviewPending === true}
            />
          ) : data.reflectionLookback !== undefined ? (
            <AttentionReflectionLookback
              cycleNumber={data.reflectionCycleNumber}
              opacityMix={opacityMix}
              pending={data.reflectionLookbackPending === true}
              rows={data.reflectionLookback}
            />
          ) : collapsibleSignals && !expanded ? null : (
            <AttentionSignals
              label={data.signalsLabel}
              opacityMix={opacityMix}
              watching={data.watching}
            />
          )}

          {data.handoff?.message ? (
            <DraftedMessage
              message={data.handoff.message}
              dividerBorder={dividerBorder}
              opacityMix={opacityMix}
            />
          ) : null}

          {/* A card whose answers are the panel has no separate primary move,
              but it can still offer a way past and a "Helpful?", and gating
              the whole row on the primary took both away with it. */}
          {data.primaryAction || (data.ghostAction && onGhost) || feedbackAction ? (
            <div
              className={cn(
                "flex flex-wrap items-center gap-2.5",
                useCompactLayout ? "mt-5" : "mt-7",
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                {/* The three primary shapes all render data.primaryAction as
                    their label, so without one they paint an empty, focusable
                    pill with no accessible name. A card whose answers are the
                    panel has no primary move and shows only the way past. */}
                {!data.primaryAction ? null : holdToComplete ? (
                  <HoldPrimaryButton
                    label={data.primaryAction}
                    hint={holdHint}
                    onPrimary={onPrimary}
                    variant={isMicrostepCard ? "filled" : "outline"}
                    surfaceBg={isLight ? undefined : primaryBg}
                    surfaceInk={isLight ? undefined : primaryInk}
                  />
                ) : data.handoff?.calendarUrl ? (
                  <a
                    href={data.handoff.calendarUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={onPrimary}
                    className={cn(
                      "group/primary inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-transparent font-sans font-semibold tracking-normal shadow-[0_2px_8px_-2px_color-mix(in_srgb,var(--color-scrim)_45%,transparent)] [transition:background-color_200ms_var(--ease-out-cubic),box-shadow_200ms_var(--ease-out-cubic),transform_120ms_var(--ease-out-cubic)] hover:-translate-y-px active:translate-y-0 active:scale-[0.96]",
                      useCompactLayout ? "px-4 py-2.5 text-sm" : "px-5 py-3 text-base",
                    )}
                    style={{ background: primaryBg, color: primaryInk }}
                  >
                    <CalendarPlus size={15} weight="regular" aria-hidden="true" />
                    {data.primaryAction}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={onPrimary}
                    className={cn(
                      "group/primary inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-transparent font-sans font-semibold tracking-normal shadow-[0_2px_8px_-2px_color-mix(in_srgb,var(--color-scrim)_45%,transparent)] [transition:background-color_200ms_var(--ease-out-cubic),box-shadow_200ms_var(--ease-out-cubic),transform_120ms_var(--ease-out-cubic)] hover:-translate-y-px hover:shadow-[0_4px_12px_-2px_color-mix(in_srgb,var(--color-scrim)_55%,transparent)] active:translate-y-0 active:scale-[0.96]",
                      useCompactLayout ? "px-4 py-2.5 text-sm" : "px-5 py-3 text-base",
                    )}
                    style={{ background: primaryBg, color: primaryInk }}
                  >
                    {showPlayIcon ? <Play size={15} weight="fill" aria-hidden="true" /> : null}
                    {data.primaryAction}
                  </button>
                )}
                {onGhost ? (
                  <button
                    type="button"
                    onClick={onGhost}
                    className={cn(
                      "inline-flex items-center justify-center whitespace-nowrap rounded-full border bg-transparent font-sans font-medium tracking-normal [transition:background-color_200ms_var(--ease-out-cubic),border-color_200ms_var(--ease-out-cubic),color_200ms_var(--ease-out-cubic),transform_120ms_var(--ease-out-cubic)] active:scale-[0.98]",
                      useCompactLayout ? "px-4 py-2.5 text-sm" : "px-5 py-3 text-base",
                    )}
                    style={{ borderColor: opacityMix(38), color: opacityMix(95) }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.backgroundColor = opacityMix(10);
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {data.ghostAction}
                  </button>
                ) : null}
                {onWhy ? (
                  <button
                    type="button"
                    onClick={onWhy}
                    className={cn(
                      "inline-flex items-center justify-center whitespace-nowrap rounded-full bg-transparent font-sans font-medium tracking-normal [transition:color_200ms_var(--ease-out-cubic)] active:scale-[0.98]",
                      useCompactLayout ? "px-3 py-2.5 text-sm" : "px-4 py-3 text-base",
                    )}
                    style={{ color: opacityMix(58) }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.color = opacityMix(95);
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.color = opacityMix(58);
                    }}
                  >
                    Why this?
                  </button>
                ) : null}
              </div>
              {!isMicrostepCard && !hasMedia && feedbackAction ? (
                <div className="ml-auto flex min-w-[140px] justify-end">{feedbackAction}</div>
              ) : null}
            </div>
          ) : null}
        </div>

        {decisions || onToggleDecisions ? (
          <DecisionsDisclosure
            decisions={decisions}
            dividerBorder={dividerBorder}
            onToggle={onToggleDecisions}
            opacityMix={opacityMix}
          />
        ) : null}

        {data.journey ? <AttentionJourney journey={data.journey} opacityMix={opacityMix} /> : null}
      </div>
    </>
  );

  return (
    <article
      className={cn(
        // Border and shadow both come from the cascade (--attn-border /
        // --attn-shadow), so a tier cannot be handed a ground without the edge
        // treatment that ground needs.
        "attention-card relative flex h-full w-full flex-col overflow-hidden rounded-2xl border sm:rounded-modal",
        // Extra room under the controls: the pill buttons are the last thing
        // on the card and square padding left them sitting on the edge, which
        // reads as clipped rather than finished.
        hasMedia ? "p-0" : "p-6 pb-8 sm:p-7 sm:pb-10 lg:p-8 lg:pb-11",
        className,
      )}
      data-tier={tier}
      data-variant={variant}
      data-surface={isLight ? "light" : "dark"}
      // The dark bundle is drawn against the light palette (paper ink on a
      // teal ground), so a dark card pins it rather than inverting under the
      // page's dark theme.
      {...(isLight ? {} : { "data-theme": "light" })}
      {...(hasMedia ? { "data-media": "" } : {})}
      aria-label="Attention card"
    >
      <span aria-hidden="true" className="attention-moonglow" />

      {!hasMedia || completed ? (
        <AttentionHeader
          data={data}
          inkSecondary={inkSecondary}
          onDismiss={!completed || isMicrostepCard ? onDismiss : undefined}
          opacityMix={opacityMix}
          tier={tier}
          tierLabel={tierLabel}
        />
      ) : null}

      {isMicrostepCard ? (
        <div className="relative z-10 grid flex-1 grid-cols-1 grid-rows-1">
          <div
            className={cn(
              "col-start-1 row-start-1 flex min-w-0 flex-col",
              completed && "pointer-events-none invisible",
            )}
            aria-hidden={completed}
          >
            {liveBody}
          </div>
          {completed ? (
            <div className="col-start-1 row-start-1 flex min-w-0 flex-col px-1 py-4">
              <CompletionState
                completedMessage={completedMessage}
                completedSubtitle={completedSubtitle}
                feedbackAction={feedbackAction}
                inkPrimary={inkPrimary}
                onDismiss={onDismiss}
                opacityMix={opacityMix}
                primaryBg={primaryBg}
                primaryInk={primaryInk}
              />
            </div>
          ) : null}
        </div>
      ) : completed ? (
        <div
          className={cn(
            "flex min-w-0 flex-1 flex-col justify-center",
            hasMedia && "px-6 py-8 sm:px-8",
          )}
        >
          <CompletionState
            completedMessage={completedMessage}
            inkPrimary={inkPrimary}
            opacityMix={opacityMix}
            primaryBg={primaryBg}
            primaryInk={primaryInk}
          />
        </div>
      ) : (
        liveBody
      )}

      {hasMedia && !completed && feedbackAction ? (
        <div className="relative z-10 mx-6 mb-6 mt-5 sm:mx-8 lg:mx-10">{feedbackAction}</div>
      ) : null}
    </article>
  );
}

function CompletionState({
  completedMessage,
  completedSubtitle,
  feedbackAction,
  inkPrimary,
  onDismiss,
  opacityMix,
  primaryBg,
  primaryInk,
}: {
  completedMessage?: string;
  completedSubtitle?: string;
  feedbackAction?: ReactNode;
  inkPrimary: string;
  onDismiss?: () => void;
  opacityMix: (pct: number) => string;
  primaryBg: string;
  primaryInk: string;
}) {
  return (
    <div className="relative z-10 flex h-full min-h-0 w-full flex-1 flex-col items-center py-6 text-center">
      {/* Message block owns the card's vertical center; controls anchor beneath. */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
        <span aria-hidden="true" className="relative grid h-[88px] w-[88px] place-items-center">
          <span
            className="absolute inset-0 rounded-full motion-safe:animate-[completion-halo-in_420ms_var(--ease-out-cubic)_120ms_both]"
            style={{ background: opacityMix(12) }}
          />
          <span
            className="relative grid h-14 w-14 place-items-center rounded-full motion-safe:animate-[completion-check-in_360ms_var(--ease-out-expo)_both]"
            style={{ background: primaryBg, color: primaryInk }}
          >
            <Check size={28} weight="bold" />
          </span>
        </span>
        <p
          className="m-0 mt-5 max-w-[36ch] text-balance font-sans text-[28px] font-medium leading-[1.15] tracking-tight motion-safe:animate-[fade-in-up_320ms_var(--ease-out-cubic)_40ms_both]"
          style={{ color: inkPrimary }}
        >
          {completedMessage ?? "Got it"}
        </p>
        {completedSubtitle ? (
          <p
            className="m-0 mt-2 line-clamp-2 max-w-[36ch] font-sans text-base italic leading-snug motion-safe:animate-[fade-in-up_320ms_var(--ease-out-cubic)_80ms_both]"
            style={{ color: opacityMix(72) }}
          >
            &ldquo;{completedSubtitle}&rdquo;
          </p>
        ) : null}
      </div>
      {feedbackAction ? (
        <div className="flex shrink-0 flex-col items-center pb-2">
          <span
            aria-hidden="true"
            className="h-px w-16 motion-safe:animate-[fade-in-up_320ms_var(--ease-out-cubic)_120ms_both]"
            style={{ background: opacityMix(18) }}
          />
          <div className="mt-5 flex flex-col items-center motion-safe:animate-[fade-in-up_320ms_var(--ease-out-cubic)_120ms_both]">
            {feedbackAction}
          </div>
        </div>
      ) : null}
      {onDismiss && !feedbackAction ? (
        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            CARD_EYEBROW,
            "mt-4 inline-flex items-center rounded-full border bg-transparent px-4 py-1.5 [transition:color_160ms_ease-out,border-color_160ms_ease-out,transform_160ms_ease-out] active:scale-[0.97] motion-safe:animate-[fade-in-up_320ms_var(--ease-out-cubic)_160ms_both]",
          )}
          style={{ borderColor: opacityMix(24), color: opacityMix(70) }}
          onMouseEnter={(event) => {
            event.currentTarget.style.color = opacityMix(92);
            event.currentTarget.style.borderColor = opacityMix(40);
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.color = opacityMix(70);
            event.currentTarget.style.borderColor = opacityMix(24);
          }}
        >
          Not now
        </button>
      ) : null}
    </div>
  );
}

function AttentionHeadline({
  data,
  inkPrimary,
  isCompactCard,
  onPrimary,
  opacityMix,
}: {
  data: AttentionCardLargeData;
  inkPrimary: string;
  isCompactCard: boolean;
  onPrimary?: () => void;
  opacityMix: (pct: number) => string;
}) {
  if (data.media) {
    return (
      <div className="attention-media-shell relative z-10 m-0">
        <AttentionMediaBlock
          headline={data.headline}
          media={data.media}
          onPlay={
            data.media.kind === "reset" || data.media.kind === "guide" ? onPrimary : undefined
          }
          subtitle={mediaSubtitleFor(data.media)}
          size="hero"
        />
      </div>
    );
  }

  const HeroIcon = data.heroIcon;
  // Colour the hero glyph by the card's behaviour where it maps (sleep, food,
  // stress, movement); clinical logs with no behaviour fall back to brand teal.
  const heroTint = data.behaviour
    ? MICROSTEP_BEHAVIOUR_VISUALS[data.behaviour].color
    : "var(--color-teal-600)";
  return (
    <div className={cn("relative z-10", isCompactCard ? "mb-4 pt-3" : "mb-5")}>
      {HeroIcon ? (
        <span
          aria-hidden="true"
          className="mb-5 grid h-16 w-16 place-items-center rounded-2xl"
          style={{
            background: `color-mix(in srgb, ${heroTint} 12%, transparent)`,
            color: heroTint,
          }}
        >
          <HeroIcon size={30} weight="duotone" />
        </span>
      ) : null}
      {data.anchor ? (
        <p
          className={cn(
            "m-0 mb-2 font-sans font-medium leading-snug",
            isCompactCard ? "text-sm" : "text-base",
          )}
          style={{ color: opacityMix(70) }}
        >
          {data.anchor}
        </p>
      ) : null}
      <h2
        className={cn(
          "m-0 text-balance font-title font-medium tracking-normal",
          isCompactCard
            ? "max-w-[22ch] text-[28px] leading-[1.08] sm:text-[32px] lg:text-[36px]"
            : "text-[26px] leading-[1.18]",
        )}
        style={{ color: inkPrimary }}
      >
        {data.headline}
      </h2>
      {/* The instruction outranks the rationale: it is the thing the member is
          being asked to do, and the "Why now" body is the argument for it. Set
          at the same size as the why block, both read as one grey paragraph
          and the ask disappeared into its own justification. */}
      {data.action ? (
        <p
          className={cn(
            // Size carries the rank, not weight: at medium the instruction
            // read as a second heading competing with the title above it.
            // A step above the why block's text-base either way, so the ask
            // outranks the argument for it.
            "m-0 mt-3 max-w-[42ch] font-sans font-normal leading-[1.45]",
            isCompactCard ? "text-xl" : "text-lg",
          )}
          style={{ color: opacityMix(96) }}
        >
          {data.action}
        </p>
      ) : null}
      {isCompactCard && data.details && data.details.length > 0 ? (
        <ul
          className={cn(CARD_EYEBROW, "mt-2.5 flex list-none flex-wrap gap-x-2.5 gap-y-1.5 p-0")}
          style={{ color: opacityMix(62) }}
        >
          {data.details.map((detail, index) => (
            <li key={detail} className="flex items-center gap-3">
              {index > 0 ? (
                <span
                  aria-hidden="true"
                  className="h-1 w-1 rounded-full"
                  style={{ background: opacityMix(48) }}
                />
              ) : null}
              <span>{detail}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function WhyBlock({
  data,
  isCompactCard,
  opacityMix,
  watchingBorder,
  expanded,
  onToggle,
  revealsSignals,
}: {
  data: AttentionCardLargeData;
  isCompactCard: boolean;
  opacityMix: (pct: number) => string;
  watchingBorder: string;
  expanded: boolean;
  onToggle: () => void;
  revealsSignals: boolean;
}) {
  const whyRef = useRef<HTMLParagraphElement | null>(null);
  // Show the expand toggle only when it reveals something: a genuinely
  // truncated rationale, or the signals panel it now also governs. A long line
  // that still fits the 2-line clamp on a wide card is not truncated, and a
  // toggle that reveals nothing is confusing.
  const clampWhy = data.clampWhy !== false;
  const canExpand = (useIsClamped(whyRef, !expanded, data.why) && clampWhy) || revealsSignals;

  if (!data.why.trim()) return null;

  if (isCompactCard) {
    return (
      <section className="relative z-10 mb-4">
        <p className={cn(CARD_EYEBROW, "m-0 mb-2")} style={{ color: opacityMix(70) }}>
          {data.whyTag}
        </p>
        <p
          ref={whyRef}
          className={cn(CARD_BODY, "m-0", clampWhy && !expanded && "line-clamp-2")}
          // Steps back when there is an instruction above it to support, so the
          // ask and its argument are not two paragraphs of the same weight.
          style={{ color: opacityMix(data.action ? 76 : 88) }}
        >
          {data.why}
        </p>
        {canExpand ? (
          <button
            type="button"
            onClick={onToggle}
            className={cn(
              CARD_EYEBROW,
              "mt-3 inline-flex items-center gap-1 rounded-full transition-colors",
            )}
            style={{ color: opacityMix(78) }}
            aria-expanded={expanded}
          >
            {expanded ? "Less context" : "More context"}
            <CaretDown
              size={13}
              className={cn(
                "transition-transform duration-[200ms] ease-[var(--ease-out-expo)]",
                expanded && "rotate-180",
              )}
            />
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <>
      <p
        className={cn(
          "relative z-10 m-0 mb-2 text-base font-medium leading-[1.62] sm:text-lg",
          data.clampWhy !== false && "line-clamp-5",
        )}
        style={{ color: opacityMix(88) }}
        title={data.why}
      >
        <span
          className={cn(
            CARD_EYEBROW,
            "mr-2.5 inline-block translate-y-[-1px] rounded-md px-2.5 py-1 align-middle",
          )}
          style={{ background: opacityMix(18), color: opacityMix(95) }}
        >
          {data.whyTag}
        </span>
        {data.why}
      </p>
      {data.methodology ? (
        <details className="relative z-10 mb-6 inline-block max-w-[60ch] align-baseline">
          <summary
            className="inline cursor-pointer list-none font-sans font-medium text-sm underline decoration-solid decoration-2 underline-offset-2 hover:decoration-[3px]"
            style={{ color: opacityMix(95) }}
          >
            Source
          </summary>
          <div
            className={cn(CARD_BODY, "mt-1.5 rounded-md p-3")}
            style={{
              background: opacityMix(14),
              color: opacityMix(92),
              border: `1px solid ${watchingBorder}`,
            }}
          >
            <p className={cn(CARD_EYEBROW, "m-0")} style={{ color: opacityMix(75) }}>
              {data.methodology.title}
            </p>
            <p className="m-0 mt-1">{data.methodology.body}</p>
            {data.methodology.source ? (
              <p className="m-0 mt-1.5 font-mono text-2xs" style={{ color: opacityMix(60) }}>
                {data.methodology.source}
              </p>
            ) : null}
          </div>
        </details>
      ) : (
        <div className="mb-4" />
      )}
    </>
  );
}

function DraftedMessage({
  message,
  dividerBorder,
  opacityMix,
}: {
  message: string;
  dividerBorder: string;
  opacityMix: (percent: number) => string;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  return (
    <div
      className="mt-5 rounded-xl border px-4 py-3.5"
      style={{ borderColor: dividerBorder, background: opacityMix(5) }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className={CARD_EYEBROW} style={{ color: opacityMix(60) }}>
          Drafted for you to send
        </span>
        <button
          type="button"
          onClick={() => {
            setCopyFailed(false);
            void navigator.clipboard
              ?.writeText(message)
              .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              })
              .catch(() => setCopyFailed(true));
          }}
          className="inline-flex min-h-6 items-center gap-1.5 rounded-full border px-2.5 py-1 font-sans text-xs font-medium active:scale-[0.97]"
          style={{ borderColor: opacityMix(30), color: opacityMix(90) }}
        >
          {copied ? (
            <Check size={12} weight="bold" aria-hidden="true" />
          ) : (
            <Copy size={12} weight="regular" aria-hidden="true" />
          )}
          {copied ? "Copied" : copyFailed ? "Select and copy" : "Copy"}
        </button>
      </div>
      <p className={cn(CARD_BODY, "mt-2")} style={{ color: opacityMix(88) }}>
        {message}
      </p>
    </div>
  );
}
